import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import logger from 'src/utils/logger';
import Modal from 'src/components/layouts/Modal/Modal';
import Textarea from 'src/components/ui/Textarea/Textarea';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { CustomerForecast, useRequestForecastChange } from 'src/services/forecasts';

interface Props {
  forecast: CustomerForecast;
  isOpen: boolean;
  onClose: () => void;
}

const MAX_REASON_LENGTH = 2000;

const RequestForecastChangeModal: React.FC<Props> = ({ forecast, isOpen, onClose }) => {
  const { t } = useTranslation();
  const mutation = useRequestForecastChange();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  const trimmed = reason.trim();
  const tooLong = reason.length > MAX_REASON_LENGTH;
  const submitDisabled = !trimmed || tooLong || mutation.isPending;

  const handleSubmit = async () => {
    try {
      await mutation.mutateAsync({ id: forecast.id, payload: { reason: trimmed } });
      toast.success(t('forecasts.requestChangeSuccess'));
      onClose();
    } catch (error) {
      logger.error('Failed to open forecast change request', error);
      toast.error(t('forecasts.requestChangeFailed'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} smallSize>
      <div className="max-w-[480px] w-full">
        <div className="p-32 flex flex-col gap-16">
          <h4 className="typo-title-24">{t('forecasts.requestChangeModal.title')}</h4>
          <p className="typo-text-14 text-faded">{t('forecasts.requestChangeModal.description')}</p>
          <Textarea
            label={t('forecasts.requestChangeModal.reasonLabel')}
            placeholder={t('forecasts.requestChangeModal.reasonPlaceholder')}
            value={reason}
            onChange={e => setReason(e.target.value)}
            error={tooLong}
            errorMessage={t('forecasts.requestChangeModal.reasonTooLong', {
              max: MAX_REASON_LENGTH,
            })}
          />
        </div>
        <div className="border-subtle-top py-16 px-24 flex justify-end gap-12">
          <Button
            tone={ButtonToneProps.SECONDARY}
            title={t('general.cancel')}
            onClick={onClose}
            disabled={mutation.isPending}
          />
          <Button
            tone={ButtonToneProps.PRIMARY_BLUE}
            title={t('forecasts.requestChangeModal.submit')}
            onClick={handleSubmit}
            disabled={submitDisabled}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RequestForecastChangeModal;
